import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import type { ProfileResponse } from "@steelbill/types"
import { ApiError, isApiError } from "@/lib/api-error"
import { api } from "@/lib/api"
import { endpoints } from "@/lib/endpoints"
import { queryKeys } from "@/lib/query-keys"

/**
 * MY OWN ACCOUNT — the client half of `/api/v1/profile` (§2, §2.1, §13).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOTHING HERE DECIDES ANYTHING, and on this surface that matters more than
 * usual. Which doors are open on an account, whether a PIN may rewrite a
 * password, whether a password is long enough — every one of those is
 * authentication, every one is the server's, and `apps/api/src/auth/
 * credential-paths.ts` records at length why those rules are deliberately NOT
 * shipped to the SPA. This module carries a request out and hands the server's
 * sentence back unedited.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* -------------------------------------------------------------------------- */
/* Reading                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `GET /profile` — the DATABASE row, not the token's claims.
 *
 * ONE KEY, NO PARAMETERS, because the endpoint has no `:id` and takes no
 * arguments: there is no request on this prefix that can be aimed at somebody
 * else's account (`lib/query-keys.ts`).
 *
 * IT IS THE ONLY SOURCE OF `doors`. `AuthUser` carries id, role and display
 * name — nothing about which sign-in methods are open — so a screen that tried
 * to work that out from the role would be `ROLE_CREDENTIAL_PATH` reimplemented
 * in a component, which is exactly the mistake this hook exists to avoid.
 */
export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile.me(),
    queryFn: async ({ signal }) => api.get<ProfileResponse>(endpoints.profile, { signal }),
  })
}

/* -------------------------------------------------------------------------- */
/* Writing                                                                     */
/* -------------------------------------------------------------------------- */

/** What a form knows about the request it last sent. Never what it sent. */
export interface ProfileWrite {
  pending: boolean
  /** The `ApiError` the server answered with, for {@link attributeProfileError}. */
  error: unknown
  /** The server's own words on success — never an error, never swallowed. */
  warnings: readonly string[]
  /** The last request succeeded and nothing has been typed since. */
  done: boolean
  /** Hand it a thunk that performs the call; the secret lives only inside it. */
  submit: (send: () => Promise<ProfileResponse>) => void
  /** Called when a field changes, so a stale success cannot sit under new input. */
  reset: () => void
}

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * DELIBERATELY NOT `useMutation`, AND THE REASON IS THE ONE RULE THIS SCREEN
 * CANNOT BEND: NO SECRET MAY OUTLIVE THE SUBMIT.
 *
 * TanStack Query keeps `mutation.variables` on the observer AFTER the mutation
 * settles — that is a feature, it is what makes `variables` readable for an
 * optimistic row or a retry button — and it lives until the next `mutate()` or
 * an explicit `reset()`. Passing `{ currentPin, pin }` through `mutate()` would
 * therefore park six digits in a cache entry attached to a mounted component,
 * for as long as the sheet stays open, where the next person to write a devtools
 * panel or an error reporter would find them. Every other screen in this app can
 * afford that; this is the one that cannot.
 *
 * So the request is handed in as a THUNK. The body is built inside the form's
 * submit handler, captured by a closure that this hook calls once and drops, and
 * the only things that survive are a boolean, a string array and an `ApiError` —
 * none of which has ever seen the secret. The forms clear their own inputs the
 * moment the request settles, so the React state does not hold one either.
 *
 * `queryKeys.profile.me()` IS SET FROM THE RESPONSE RATHER THAN INVALIDATED. The
 * body of every write on this prefix is the freshly-read row (`present()` in
 * services/profile.ts), so a refetch would be a second round trip to learn what
 * we have just been told.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useProfileWrite(): ProfileWrite {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [warnings, setWarnings] = useState<readonly string[]>([])
  const [done, setDone] = useState(false)

  /*
   * A profile write is a bcrypt comparison on a single-threaded server (~250ms)
   * and the sheet can be closed while it is in flight. Without this, the answer
   * lands on an unmounted component.
   */
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setWarnings([])
    setDone(false)
  }, [])

  const submit = useCallback(
    (send: () => Promise<ProfileResponse>) => {
      setPending(true)
      setError(null)
      setWarnings([])
      setDone(false)

      void send().then(
        (answer) => {
          queryClient.setQueryData(queryKeys.profile.me(), answer)
          if (!mounted.current) return
          setPending(false)
          setWarnings(answer.warnings ?? [])
          setDone(true)
        },
        (failure: unknown) => {
          if (!mounted.current) return
          setPending(false)
          setError(failure)
        },
      )
    },
    [queryClient],
  )

  return { pending, error, warnings, done, submit, reset }
}

/* -------------------------------------------------------------------------- */
/* Which field was refused                                                     */
/* -------------------------------------------------------------------------- */

/** Where one refusal belongs on the form that caused it. */
export interface ProfileRefusal {
  /** Against the CURRENT-secret input. */
  current: string | null
  /** Against the new secret / new identifier input. */
  next: string | null
  /** Belongs to no single input — a closed door, a rate limit, a crossing. */
  form: string | null
  /** The server said nothing usable (5xx, or the request never landed). */
  generic: boolean
}

const NOTHING: ProfileRefusal = { current: null, next: null, form: null, generic: false }

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * A WRONG CURRENT SECRET MUST NOT READ AS THE NEW ONE BEING REJECTED.
 *
 * The server's refusal is `401 That is not your current PIN or password.` — a
 * deliberately generic sentence, identical whichever door was presented, so that
 * it confirms nothing to somebody holding a borrowed session. Shown in a banner
 * at the foot of a form whose other input is labelled "New PIN", that sentence
 * reads as "your new PIN was rejected", and the person retypes the wrong half:
 * every retype costs another bcrypt comparison and another count against the
 * per-account lockout, so the misreading is what freezes the account.
 *
 * A 401 FROM THESE TWO ROUTES IS NEVER A SESSION PROBLEM. It cannot be — the
 * requests carry `reauthenticating: true`, so `lib/api.ts` has neither refreshed
 * nor expired anything (its own comment spells out that a mistyped digit would
 * otherwise sign the owner out). There is exactly one thing a 401 here can mean,
 * and it is the current-secret field.
 *
 * EVERYTHING ELSE KEEPS THE SERVER'S OWN WORDS AND ITS OWN FIELD NAME. A 400
 * names the field (`pin`, `phone`, `currentPassword`) and the message goes
 * against that input; a 403 (a PIN may not rewrite a password — which this
 * screen never offers) and a 409 (that door is not open — an administrator's
 * decision) belong to no input, and their sentences say what to do instead.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function attributeProfileError(
  error: unknown,
  currentField: "currentPin" | "currentPassword",
  nextField: "pin" | "password" | "phone" | "email",
): ProfileRefusal {
  if (error === null || error === undefined) return NOTHING
  if (!isApiError(error)) return { ...NOTHING, generic: true }

  const failure: ApiError = error
  // `lib/api.ts` has already discarded a 5xx body; there is nothing to show.
  if (failure.isServerError) return { ...NOTHING, generic: true }
  if (failure.isNetworkFailure) return { ...NOTHING, form: failure.message }

  const fields = failure.fieldErrors
  const current = fields[currentField] ?? null
  const next = fields[nextField] ?? null

  if (failure.status === 401) {
    return { current: current ?? failure.message, next, form: null, generic: false }
  }
  if (current !== null || next !== null) {
    return { current, next, form: null, generic: false }
  }
  return { ...NOTHING, form: failure.message }
}
